"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import React, { useCallback, useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import InputWithSuffix from "@/components/form/form-input-sufix";
import { SliderExtended } from "@/components/slider-extended";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { FilterRangePreset } from "./filter-range.types";

type FilterRangeProps = {
  labelStart: string;
  labelEnd: string;
  paramStart: string;
  paramEnd: string;
  unit: string;
  maxValue?: number;
  minValue?: number;
  step?: number;
  rangePresets?: FilterRangePreset[];
};

export const FilterRangeAutoupdate = ({
  paramStart,
  paramEnd,
  labelStart,
  labelEnd,
  unit,
  maxValue = 120,
  minValue = 0,
  step = 5,
  rangePresets,
}: FilterRangeProps) => {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { replace } = useRouter();
  const [localValues, setLocalValues] = useState<number[]>([
    Number(searchParams.get(paramStart)) || minValue,
    Number(searchParams.get(paramEnd)) || maxValue,
  ]);

  const updateSearchParams = useCallback(
    (values: number[]) => {
      if (values.length !== 2) {
        return;
      }

      const [startValue, endValue] = values;

      if (startValue === undefined || endValue === undefined) {
        return;
      }

      const params = new URLSearchParams(searchParams);

      params.set(paramStart, startValue.toString());
      params.set(paramEnd, endValue.toString());
      params.delete("page");

      replace(`${pathname}?${params.toString()}`, { scroll: false });
    },
    [pathname, paramEnd, paramStart, replace, searchParams],
  );

  const handleValueChange = useCallback(
    (values: number[] = localValues) => {
      updateSearchParams(values);
    },
    [localValues, updateSearchParams],
  );

  const handleDebounceChange = useDebouncedCallback((values: number[]) => {
    updateSearchParams(values);
  }, 500);

  useEffect(() => {
    setLocalValues([
      Number(searchParams.get(paramStart)) || minValue,
      Number(searchParams.get(paramEnd)) || maxValue,
    ]);
  }, [maxValue, minValue, paramEnd, paramStart, searchParams]);

  const handleInputChange = useCallback(
    (index: number, value: number) => {
      setLocalValues((prev) => {
        const nextValues = [...prev];
        nextValues[index] = value;
        handleDebounceChange(nextValues);
        return nextValues;
      });
    },
    [handleDebounceChange],
  );

  const handlePresetClick = useCallback(
    (preset: FilterRangePreset) => {
      const nextValues = [preset.min, preset.max];
      setLocalValues(nextValues);
      handleValueChange(nextValues);
    },
    [handleValueChange],
  );

  const handleSliderChange = useCallback((values: number[]) => {
    if (values.length !== 2) {
      return;
    }

    setLocalValues(values);
  }, []);

  return (
    <div className="grid w-full gap-4">
      {rangePresets?.length ? (
        <ol className="mb-2 grid grid-cols-2 gap-2">
          {rangePresets.map((preset) => {
            const isSelected =
              localValues[0] === preset.min && localValues[1] === preset.max;

            return (
              <li key={`${preset.label}-${preset.min}-${preset.max}`}>
                <Button
                  type="button"
                  variant="outline"
                  aria-pressed={isSelected}
                  className={cn(
                    "h-12 w-full border-transparent px-2 font-normal text-base",
                    isSelected && "border-primary",
                  )}
                  onClick={() => handlePresetClick(preset)}
                >
                  {preset.label}
                </Button>
              </li>
            );
          })}
        </ol>
      ) : null}
      <SliderExtended
        value={localValues}
        minStepsBetweenThumbs={1}
        max={maxValue}
        min={minValue}
        step={step}
        onValueChange={handleSliderChange}
        onPointerUp={() => handleValueChange()}
        className={cn("w-full")}
        classNameTrack="bg-white h-0.5"
        classNameThumb="bg-brand-green2-300 h-5 w-5 "
      />

      <div className="mt-2 flex flex-wrap gap-4">
        <ol className="flex w-full items-center gap-4">
          {localValues.map((_, index) => (
            <React.Fragment key={index}>
              <li className="flex h-14 flex-1 flex-col items-start rounded-xl bg-white px-4 py-2">
                <span className="text-black/50">
                  {index % 2 === 0 ? labelStart : labelEnd}
                </span>
                <InputWithSuffix
                  id={unit}
                  unit={unit}
                  initialValue={localValues[index]}
                  className="-mx-2 -my-0.5 w-full px-2 py-0.5 text-base leading-5"
                  callback={(value: number) => handleInputChange(index, value)}
                  min={index === 0 ? minValue : localValues[0]}
                  max={index === 1 ? maxValue : localValues[1]}
                />
              </li>
              {index % 2 === 0 ? (
                <li className="h-0.5 shrink-0 basis-4 bg-gray-300"></li>
              ) : null}
            </React.Fragment>
          ))}
        </ol>
      </div>
    </div>
  );
};
